/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { widgetById, isCustomId, METRIC_COLORS } from "../../Homepage/customize/widgetRegistry";
import { UseCompanyWorkspaces } from "../../../hooks/company";
import LeaderboardSetup, { useLiveColumns } from "../../Homepage/customize/LeaderboardSetup";
import ScopeWindow from "../../Homepage/customize/ScopeWindow";
import MetricPlanEditor from "../../Homepage/customize/MetricPlanEditor";
import { EarningsSetup, LadderSetup } from "../../Homepage/customize/MoneySetup";
import LaborSetup, { laborConfigured, LaborFormulaCard } from "../../Homepage/customize/LaborSetup";
import { MetricPicker, metricsForWorkspaces } from "../../Homepage/customize/MetricPicker";
import ProfitTrendSetup, { profitTrendConfigured, ProfitFormulaCard } from "../../Homepage/customize/ProfitTrendSetup";
import NetProfitBonusSetup, { netBonusSummary } from "../../Homepage/customize/NetProfitBonusSetup";
import { laborModeOf, resolveLaborWidget, revenueModeOf } from "../../../components/ProfitTrend/data";
import { SERIES as PROFIT_SERIES } from "../../../components/ProfitTrend/TrendChart";
import DeptMetricsSetup, { DeptMetricsFormulaCard, DeptTileStyle } from "../../Homepage/customize/DeptMetricsSetup";
import {
  colsOf, includesSubWorkspaces, pickedWorkspaceIds, subsExplicit,
} from "../../../components/DeptMetrics/data";
import { revenueKpiOf, revenueOf, rowsOf } from "../../../components/LaborMetrics/data";
import CombinedEarningsSetup from "../../Homepage/customize/CombinedEarningsSetup";
import AnnualCompSetup from "../../Homepage/customize/AnnualCompSetup";
import { CustomFields, CustomLayout, GraphPicker } from "../../Homepage/customize/CustomizeDrawer";
import { FETCH_BONUS_PROGRESS } from "../../../api/group";
import {
  programFromConfig,
  sanitizeProgram,
  sanitizeCombinedPay,
  emptyCombinedPay,
  hasCombinedPayRules,
  seedCombinedPayFromPrograms,
  combinedPayToPrograms,
} from "../../../components/SalesBonus/moneyRules";
import { moneyMetricsForWorkspace } from "../../../helpers/moneyMetrics";
import { realReps } from "../../../helpers/teamAverage";
import { formatAxis, parseStep, stepPresets } from "../../../components/GoalProgress/axisTicks";
import { LOOK_AHEAD_OPTIONS, LOOK_AHEAD_STYLES, isScheduledRevenueName } from "../../../components/GoalProgress/lookAhead";
import "../../../components/GoalProgress/goalProgress.css";

/**
 * Edit panel for a component on an Analytics dashboard — the SAME two-tab
 * editor the workspace dashboard's drawer uses (gmbw-etabs / gmbw-fld /
 * gmbw-scenes, customize.css). Edits apply to the card LIVE as you change
 * them; Cancel restores how it was, Save flips to "Saved ✓" and closes.
 */

const IcBack = "M15 18l-6-6 6-6";
const IcX = "M18 6 6 18M6 6l12 12";
const Icon = ({ d, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
Icon.propTypes = { d: PropTypes.string.isRequired, size: PropTypes.number };

/* Goal Progress's two line colours — same swatch row idiom CustomizeDrawer's
   own metric ColourPicker uses (.gmbw-swatches/.gmbw-swatch), plus a native
   colour-input swatch at the end for anything outside the 7-colour palette
   ("the colors are limited" — the fixed palette alone wasn't enough). Plain
   hex only, no company/goal/none sentinels — those answer "how does a
   metric's value compare", meaningless for a line that's just decoration. */
const LineColorSwatches = ({ label, value, defaultColor, onChange }) => {
  const isPaletteColor = METRIC_COLORS.includes(value);
  const customValue = value && !isPaletteColor ? value : defaultColor;
  return (
    <div className="gmbw-fld">
      <label>{label}</label>
      <div className="gmbw-swatches" role="group" aria-label={label}>
        {METRIC_COLORS.map((c) => {
          const on = (value || defaultColor) === c;
          return (
            <button
              key={c}
              type="button"
              className={`gmbw-swatch${on ? " on" : ""}`}
              style={{ background: c }}
              onClick={() => onChange(c)}
              aria-label={`Use ${c}`}
              aria-pressed={on}
            />
          );
        })}
        <label
          className={`gprog-swatch-custom${value && !isPaletteColor ? " on" : ""}`}
          style={value && !isPaletteColor ? { background: value } : undefined}
          title="Custom colour"
        >
          <input
            type="color"
            value={customValue}
            onChange={(e) => onChange(e.target.value)}
            aria-label={`${label}: custom colour`}
          />
          {(!value || isPaletteColor) && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="13.5" cy="6.5" r="2.5" /><circle cx="19" cy="13" r="2.5" /><circle cx="6" cy="12" r="2.5" /><circle cx="10" cy="19" r="2.5" />
              <path d="M12 2a10 10 0 1 0 8.9 14.6 2 2 0 0 0-1.8-2.9h-1a2 2 0 0 1-2-2 2 2 0 0 1 2-2h2.5A9.9 9.9 0 0 0 12 2Z" />
            </svg>
          )}
        </label>
      </div>
    </div>
  );
};
LineColorSwatches.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  defaultColor: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
LineColorSwatches.defaultProps = { value: null };

/* Goal Progress's Y-axis gap — Kelvin's ask (2026-09-29): "5k 10k 15k (by
   5k)", with a choice of what the gap is. Auto (round steps that fit, the
   default), a few quick picks, or any number typed in. The chart side is
   components/GoalProgress/axisTicks.js.

   The quick picks are sized to THIS card's goal and view (stepPresets) — a
   fixed $1k…$50k list meant nothing against a $300k running total, where
   every one of them was too fine to draw and they all came out the same.
   The fixed lists below are only for a card with no goal yet. */
const Y_STEP_FALLBACK = {
  $: [1000, 5000, 10000, 25000, 50000],
  "%": [5, 10, 20, 25],
  "#": [1, 5, 10, 25, 100],
};
const YStepField = ({ value, kind, goal, view, onChange }) => {
  const sized = stepPresets({ goal, view });
  const presets = sized.length ? sized : (Y_STEP_FALLBACK[kind] || Y_STEP_FALLBACK["#"]);
  const stored = parseStep(value);
  /* Custom stays open while its box is empty mid-edit — keyed off a flag, not
     off the value, or clearing the box to retype would snap back to Auto. */
  const [customClicked, setCustomOpen] = useState(false);
  /* A stored step outside today's quick picks (typed, or picked before the
     goal or view changed what the picks are) is shown as Custom. */
  const customOpen = customClicked || (stored != null && !presets.includes(stored));
  const pick = (v) => { setCustomOpen(false); onChange(v); };
  const unit = { $: "dollars", "%": "percentage points" }[kind] || "units";
  return (
    <div className="gmbw-fld">
      <label>Y-axis steps</label>
      <div className="gmbw-seg gprog-ystep" role="group" aria-label="Y-axis steps">
        <button type="button" className={`gmbw-segbtn${!customOpen && stored == null ? " on" : ""}`} aria-pressed={!customOpen && stored == null} onClick={() => pick(null)}>
          Auto
        </button>
        {presets.map((p) => {
          const on = !customOpen && stored === p;
          return (
            <button key={p} type="button" className={`gmbw-segbtn${on ? " on" : ""}`} aria-pressed={on} onClick={() => pick(p)}>
              {formatAxis(p, kind)}
            </button>
          );
        })}
        <button type="button" className={`gmbw-segbtn${customOpen ? " on" : ""}`} aria-pressed={customOpen} onClick={() => setCustomOpen(true)}>
          Custom
        </button>
      </div>
      {customOpen && (
        <input
          type="number"
          min="0"
          step="any"
          inputMode="decimal"
          className="gprog-ystep-input"
          aria-label={`Gap between Y-axis labels, in ${unit}`}
          value={value ?? ""}
          placeholder={kind === "$" ? "e.g. 2500" : "e.g. 20"}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      <p className="gmbw-hint">
        Auto picks round numbers that fit the goal and the data. A fixed step puts a label at every
        multiple of it — the quick picks are sized to this card&apos;s goal. A custom step too fine for
        the card&apos;s height to print skips to every 2nd, 5th or 10th multiple rather than overlap.
      </p>
    </div>
  );
};
YStepField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  kind: PropTypes.oneOf(["$", "%", "#"]),
  goal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  view: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
YStepField.defaultProps = { value: null, kind: "#", goal: null, view: "cumulative" };

// Components that read the app's selected workspace through their own hooks.
const SELECTED_WS_COMPS = new Set(["podium"]);

/* Goal Progress chart styles that plot a SERIES over days, as opposed to the
   single-value shapes GoalShape.jsx draws. Keep in sync with GoalProgress's
   own SHAPE_STYLES (components/GoalProgress/index.jsx) — the split is the same
   one, read from the other side. */
const GOAL_SERIES_STYLES = ["area", "line", "bar", "spark"];

export default function ComponentEditor({ widget, onChange, onBack, onClose, metricCatalog = [], boardWidgets = [] }) {
  const workspaces = UseCompanyWorkspaces(true) || [];
  const def = widgetById(widget.comp);
  const scenes = def?.scenes || [];
  const opts = widget.options || {};
  const followsSelected = SELECTED_WS_COMPS.has(widget.comp);

  const isMoney = !!def?.money;
  const isAnnual = !!def?.annualSetup;
  /* Labor picks a workspace per department row in its own Setup, so it takes
     none of the workspace controls Basics offers the other cards. */
  const isLabor = !!def?.laborSetup;
  const isGoalCard = !!def?.goalCard;
  /* Total Scheduled Revenue is the only metric with a look-ahead option. */
  const pickedMetricId = opts.metrics && opts.metrics[0] && opts.metrics[0].metricId;
  const pickedMetric = (metricCatalog || []).find((m) => m.id === pickedMetricId);
  const isScheduledMetric = isGoalCard && isScheduledRevenueName(pickedMetric?.title || pickedMetric?.name || pickedMetricId);
  /* Revenue, Expenses & Labor picks its own sources (and every goal) in Setup,
     so like Labor it takes none of Basics' single-workspace dropdown. */
  const isProfit = !!def?.profitSetup;
  /* Net Profit Bonus reads its Net profit from a profit card on the board and
     picks everything else in its own Setup — no workspace dropdown either. */
  const isNetBonus = !!def?.netBonusSetup;
  /* All Department Metrics picks a workspace per ROW and its metrics per row,
     so like Labor it takes none of Basics' single-workspace dropdown. */
  const isDept = !!def?.deptSetup;
  const scopedWs = (opts.workspaceIds || []).filter(Boolean).map(String);
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  const moneyWsIds = useMemo(() => {
    if (scopedWs.length) return scopedWs;
    if (opts.workspaceId) return [String(opts.workspaceId)];
    return [];
  }, [scopedWs, opts.workspaceId]);
  const [moneyRaw, setMoneyRaw] = useState({});

  const packFromResponse = (wsId, res) => {
    /* Card-local rules win. Workspace bonusConfig is only the seed until this
       Analytics card has its own Setup saved on the widget. */
    const local = opts.bonusPrograms && opts.bonusPrograms[wsId]
      ? sanitizeProgram(opts.bonusPrograms[wsId])
      : null;
    const program = local || programFromConfig(res.config);
    const metrics = moneyMetricsForWorkspace(wsId, companyKpis, res.details || {});
    const reps = realReps(res.reps || []);
    const my = (res.reps || []).find((p) => p && p.isMe) || reps[0] || (res.reps || [])[0];
    let previewYtd = 0;
    if (reps.length >= 2) {
      previewYtd = reps.reduce((s, r) => s + (Number(r.ytd) || 0), 0) / reps.length;
    } else if (my) {
      previewYtd = Number(my.ytd) || 0;
    }
    const scored = metrics.find((m) => m.id === String(program.scoredOnKpiId || res.config?.revenueKpiId || ""));
    const cutId = (program.commissions || []).map((c) => c && c.kpiId).find(Boolean) || null;
    const cut = cutId ? metrics.find((m) => m.id === String(cutId)) : null;
    return {
      program,
      metrics,
      ytd: previewYtd,
      scoredOnLabel: scored ? scored.label : null,
      earningsScoredOnLabel: cut ? cut.label : null,
      fromWorkspace: !local,
    };
  };

  useEffect(() => {
    if (!isMoney || !moneyWsIds.length) {
      setMoneyRaw({});
      return undefined;
    }
    let alive = true;
    Promise.all(
      moneyWsIds.map((id) =>
        FETCH_BONUS_PROGRESS(id).then((res) => [id, res]).catch(() => [id, null]),
      ),
    ).then((pairs) => {
      if (!alive) return;
      const next = {};
      pairs.forEach(([id, res]) => {
        if (res) next[id] = res;
      });
      setMoneyRaw(next);
    });
    return () => { alive = false; };
    /* Only the workspace SET decides what to fetch. companyKpis and
       opts.bonusPrograms only change how an already-fetched response is
       read (see packFromResponse/moneyPacks below) — they used to sit in
       this effect's deps too, which requeued a FETCH_BONUS_PROGRESS round
       trip per workspace on every keystroke in Setup (bonusPrograms is
       rewritten on every edit, see onMoneyChange/onCombinedPayChange), and
       that's what made Setup feel like it never finished loading. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoney, moneyWsIds.join("|")]);

  /* Pure, local re-derivation from the last fetch — no network — so typing
     in Setup (which only touches opts.bonusPrograms/companyKpis) re-reads
     the cached response instead of re-fetching it. */
  const moneyPacks = useMemo(() => {
    const out = {};
    Object.entries(moneyRaw).forEach(([id, res]) => {
      out[id] = packFromResponse(id, res);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moneyRaw, companyKpis, JSON.stringify(opts.bonusPrograms || {})]);

  const moneyMeta = useMemo(() => {
    if (!moneyWsIds.length) return { scoredOnLabel: null, earningsScoredOnLabel: null };
    if (moneyWsIds.length > 1) {
      const pay = opts.combinedPay && hasCombinedPayRules(opts.combinedPay)
        ? sanitizeCombinedPay(opts.combinedPay)
        : null;
      const firstBonus = pay && pay.bonuses && pay.bonuses[0];
      const firstCut = pay && pay.commissions && pay.commissions[0];
      return {
        scoredOnLabel: "Combined — see Setup",
        earningsScoredOnLabel:
          (firstBonus && firstBonus.label)
          || (firstCut && firstCut.label)
          || "Combined — see Setup",
      };
    }
    const pack = moneyPacks[moneyWsIds[0]];
    return pack
      ? { scoredOnLabel: pack.scoredOnLabel, earningsScoredOnLabel: pack.earningsScoredOnLabel }
      : { scoredOnLabel: null, earningsScoredOnLabel: null };
  }, [moneyWsIds, moneyPacks, opts.combinedPay]);

  const moneyLoading = isMoney && moneyWsIds.length > 0 && moneyWsIds.some((id) => !moneyPacks[id]);

  /* Analytics cards own their pay rules on the widget. Never write Group.bonusConfig
     — that made every dashboard / Homepage sharing the workspace fight over one setup. */
  const onMoneyChange = (wsId, next) => {
    const clean = sanitizeProgram(next);
    onChange({
      title: widget.title,
      options: {
        ...opts,
        bonusPrograms: {
          ...(opts.bonusPrograms || {}),
          [String(wsId)]: clean,
        },
      },
    });
  };

  /* Multi-ws Anticipated earnings: one combined rule list (metric per workspace),
     mirrored into bonusPrograms so bonus-progress still loads every sourced KPI. */
  const onCombinedPayChange = (next) => {
    const clean = sanitizeCombinedPay(next);
    const programs = combinedPayToPrograms(clean, moneyWsIds);
    onChange({
      title: widget.title,
      options: {
        ...opts,
        combinedPay: clean,
        bonusPrograms: {
          ...(opts.bonusPrograms || {}),
          ...programs,
        },
        /* Combined rules already are one line — drop the old display-only merge flag. */
        breakdownSplit: false,
      },
    });
  };

  const combinedPayLive = useMemo(() => {
    if (!(isMoney && def?.money === "earnings" && moneyWsIds.length >= 2)) return null;
    if (opts.combinedPay && hasCombinedPayRules(opts.combinedPay)) {
      return sanitizeCombinedPay(opts.combinedPay);
    }
    if (opts.combinedPay && typeof opts.combinedPay === "object") {
      return sanitizeCombinedPay(opts.combinedPay);
    }
    /* First open on a legacy multi-ws card: seed from per-ws bonusPrograms. */
    if (opts.bonusPrograms && Object.keys(opts.bonusPrograms).length) {
      return seedCombinedPayFromPrograms(opts.bonusPrograms, moneyWsIds);
    }
    return emptyCombinedPay();
  }, [isMoney, def?.money, moneyWsIds, opts.combinedPay, opts.bonusPrograms]);

  const combinedMetricsByWs = useMemo(() => {
    const map = {};
    moneyWsIds.forEach((id) => {
      map[id] = (moneyPacks[id] && moneyPacks[id].metrics) || [];
    });
    return map;
  }, [moneyWsIds, moneyPacks]);

  const combinedWsNames = useMemo(() => {
    const map = {};
    moneyWsIds.forEach((id) => {
      const ws = workspaces.find((w) => String(w._id) === id);
      map[id] = (ws && ws.name) || "Workspace";
    });
    return map;
  }, [moneyWsIds, workspaces]);

  /* The leaderboard carries rules of its own, so it gets the same third tab
     the workspace dashboard's editor gives it. Same component, same editor. */
  const board = def?.board || null;
  const isLeaderboard = widget.comp === "leaderboard";
  const across = isLeaderboard && !!opts.across;
  /* Only a CROSS-workspace board has columns, ranking rules and switches. A
     one-workspace board is the plain leaderboard: nothing to configure, so
     nothing is offered. */
  const isBoard = across && board?.setup === "leaderboard";
  const [tab, setTab] = useState("basics");
  const live = useLiveColumns(opts, isBoard);
  const [flash, setFlash] = useState(false);
  const [openLabels, setOpenLabels] = useState({});
  const toggleLabel = (key) => setOpenLabels((o) => ({ ...o, [key]: !o[key] }));
  // Snapshot at open, so Cancel puts the card back exactly as it was.
  const openedWith = useRef({ title: widget.title, options: widget.options });

  useEffect(() => {
    openedWith.current = { title: widget.title, options: widget.options };
    setTab("basics");
    setOpenLabels({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.i]);

  /* Flipping to "One workspace" removes the Setup tab — don't strand the
     editor on a tab that no longer exists.

     This list MUST match the condition that renders the tab below. Miss a card
     off it and the tab is drawn but unusable: the click sets "setup", this
     effect fires on the very next render and puts it straight back on Basics,
     so the button looks dead. */
  useEffect(() => {
    if (!isBoard && !isMoney && !isAnnual && !isLabor && !isProfit && !isNetBonus && !isDept && tab === "setup") setTab("basics");
  }, [isBoard, isMoney, isAnnual, isLabor, isProfit, isNetBonus, isDept, tab]);

  const cancel = () => {
    onChange(openedWith.current);
    onBack();
  };
  const save = () => {
    setFlash(true);
    setTimeout(() => {
      setFlash(false);
      onBack();
    }, 650);
  };

  const setOpt = (patch) => onChange({ title: widget.title, options: { ...opts, ...patch } });
  const setTitle = (title) => onChange({ title, options: opts });
  /* ScopeWindow + MetricPlanEditor pass the full settings blob — replace options. */
  const setOpts = (next) => onChange({ title: widget.title, options: next });

  const tops = workspaces.filter((w) => !w.parentId);
  const multiWs = (opts.workspaceIds || []).filter(Boolean).length >= 2;

  /* One line under Basics saying what this card is actually reading, so an
     admin can see it's wired up without opening Setup — and, more usefully,
     see when it ISN'T. */
  const netBonusLine = useMemo(
    () => (isNetBonus ? netBonusSummary(opts, boardWidgets) : ""),
    [isNetBonus, opts, boardWidgets],
  );
  const profitSummary = useMemo(() => {
    if (!isProfit) return "";
    if (!profitTrendConfigured(opts)) return "Not set";
    const revMode = revenueModeOf(opts);
    const rev = { auto: "Total Revenue metric", pick: "a metric you picked", manual: "typed revenue" }[revMode];
    let labor = "typed labor";
    if (laborModeOf(opts) === "widget") {
      /* The same resolution the card runs, so Basics can't claim a source the
         card itself refuses to use — one payroll card on the board needs no
         pick, two mean this reads "pick a payroll card" until one is made. */
      const { widget: card, missing } = resolveLaborWidget(opts, boardWidgets);
      if (card) labor = `labor from "${card.title || "Payroll vs Revenue"}"`;
      else labor = missing ? "no payroll card on this board" : "pick a payroll card";
    }
    const n = (opts.expenses || []).filter((e) => e && Number(e.amount) > 0).length;
    return `${rev} · ${labor} · ${n} expense${n === 1 ? "" : "s"}`;
  }, [isProfit, opts, boardWidgets]);
  /* One line under Basics saying what the card is actually tracking, so an
     admin can see it's wired up without opening Setup. */
  const deptSummary = useMemo(() => {
    if (!isDept) return "";
    const picked = pickedWorkspaceIds(opts);
    const who = picked.length
      ? `${picked.length} workspace${picked.length === 1 ? "" : "s"}`
      : "Every workspace";
    /* Once a company has customized which sub-workspaces show (subsExplicit),
       `picked` already names them individually — an unconditional "+
       sub-workspaces" suffix would then double-count what "who" just said. */
    const subs = !subsExplicit(opts) && includesSubWorkspaces(opts) ? " + sub-workspaces" : "";
    return `${who}${subs} · up to ${colsOf(opts)} metrics each`;
  }, [isDept, opts]);

  return (
    <>
      <div className="gmbw-panel-h">
        <div className="gmbw-panel-htxt">
          <button type="button" className="gmbw-backlink" onClick={cancel}>
            <Icon d={IcBack} size={13} /> All components
          </button>
          <span className="gmbw-panel-title">Edit component</span>
          <span className="gmbw-panel-sub">{def?.title || "Component"}</span>
        </div>
        <button type="button" className="gmbw-panel-x" onClick={onClose} aria-label="Close">
          <Icon d={IcX} size={15} />
        </button>
      </div>

      <div className="gmbw-live">
        <i />
        Watch the card on the board — it changes as you edit
      </div>

      <div className="gmbw-etabs">
        <button type="button" className={tab === "basics" ? "on" : ""} onClick={() => setTab("basics")}>
          Basics
        </button>
        {(isBoard || isMoney || isAnnual || isLabor || isProfit || isNetBonus || isDept) && (
          <button type="button" className={tab === "setup" ? "on" : ""} onClick={() => setTab("setup")}>
            Setup
          </button>
        )}
        <button type="button" className={tab === "appearance" ? "on" : ""} onClick={() => setTab("appearance")}>
          Appearance
        </button>
      </div>

      <div className="gmbw-edit-body">
        {tab === "basics" && (
          <>
            <div className="gmbw-fld">
              <label htmlFor="dbce-title">Title on the card</label>
              <input
                id="dbce-title"
                type="text"
                value={widget.title || ""}
                placeholder={def?.title || ""}
                maxLength={60}
                onChange={(e) => setTitle(e.target.value)}
              />
              <p className="gmbw-hint">Leave empty to keep “{def?.title}”.</p>
            </div>

            {(isCustomId(widget.comp) || def?.custom) && (
              <CustomFields
                draft={opts}
                metricCatalog={metricCatalog || []}
                onChange={(next) => {
                  /* Custom cards use workspaceIds (multi). Drop a stale single
                     workspaceId so we never keep "Technicians" after you picked
                     Sales + In-House — that leftover picker used to sit below. */
                  const ids = (next.workspaceIds || []).filter(Boolean).map(String);
                  const patch = { ...next };
                  if (ids.length) {
                    const host = String(patch.workspaceId || opts.workspaceId || "");
                    if (!ids.includes(host)) {
                      const [firstId] = ids;
                      patch.workspaceId = firstId;
                    }
                  }
                  setOpt(patch);
                }}
              />
            )}

            {/* Workspaces first, then metric pairing — same order as Home. Putting
                Metrics below date range hid it below the fold (scrollbar is off).
                Money + annual-comp cards are NOT metric boards: they get their own
                ScopeWindow below (or use scope without MetricPlan). Never stack a
                second ScopeWindow on top of def.scope — that mangled Basics. */}
            {def?.scope && !isMoney && !isAnnual && (
              <ScopeWindow draft={opts} onChange={setOpts} fields={["workspaces"]} />
            )}

            {isGoalCard && (
              <>
                <div className="gmbw-fld">
                  <label>Metric</label>
                  <MetricPicker
                    metrics={metricsForWorkspaces(metricCatalog, opts.workspaceIds, opts.workspaceId)}
                    value={(opts.metrics && opts.metrics[0] && opts.metrics[0].metricId) || null}
                    onPick={(m) => setOpt({
                      metrics: [{ metricId: m.id, graph: "number", color: null }],
                      /* A metric row can't carry its own workspaceIds — sanitizeSettings
                         strips anything off a metric row besides metricId/graph/color
                         (layoutStore.js). So the first pick (only if the admin hasn't
                         already chosen a scope) seeds the card's OWN workspace scope
                         from the metric's real home workspace(s) instead — otherwise
                         the card silently read whatever workspace the board defaults
                         to, which for a metric tracked elsewhere (Total Revenue on
                         Sales, board defaulted to Technicians) read a same-named but
                         unrelated/near-empty KPI and showed "$19" next to a real
                         $256,577. Visible and editable in Workspaces above. */
                      workspaceIds: (opts.workspaceIds || []).length
                        ? opts.workspaceIds
                        : (m.workspaceIds || []).filter(Boolean).map(String),
                    })}
                  />
                  <p className="gmbw-hint">Which tracked KPI this card measures against your goal.</p>
                </div>
                <div className="gmbw-fld">
                  <label htmlFor="dbce-goal">
                    {(() => {
                      const picked = metricCatalog.find((m) => m.id === (opts.metrics && opts.metrics[0] && opts.metrics[0].metricId));
                      if (picked?.kind === "$") return "Goal ($)";
                      if (picked?.kind === "%") return "Goal (%)";
                      return "Goal";
                    })()}
                  </label>
                  <input
                    id="dbce-goal"
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    value={opts.goalTarget ?? ""}
                    placeholder={(() => {
                      const picked = metricCatalog.find((m) => m.id === (opts.metrics && opts.metrics[0] && opts.metrics[0].metricId));
                      if (picked?.derived && picked.kind === "%") return "e.g. 60";
                      if (picked?.derived) return "e.g. 175";
                      return "e.g. 50000";
                    })()}
                    onChange={(e) => setOpt({ goalTarget: e.target.value })}
                  />
                  <p className="gmbw-hint">What &quot;done&quot; looks like for this metric — the trend line is drawn against this number.</p>
                </div>
              </>
            )}

            {def?.scope && !isMoney && !isAnnual && def.id !== "kotm" && multiWs && (
              <MetricPlanEditor draft={opts} onChange={setOpts} metricCatalog={metricCatalog} />
            )}

            {/* "Card dropdown shows" (admin/player) only means anything for a card
                that actually puts a workspace/player switcher ON the card itself
                (KOTM, Beat the Yeti…) — Goal Progress has no such dropdown, so
                the field did nothing and Kelvin caught it sitting there dead. */}
            {def?.scope && !isMoney && !isAnnual && (
              <ScopeWindow draft={opts} onChange={setOpts} fields={isGoalCard ? ["show", "range"] : ["admin", "show", "range"]} />
            )}

            {isMoney && (
              <>
                <div className="gmbw-fld">
                  <label>Scored on</label>
                  <div className="gmbw-lockedfield">
                    <span>{(def.money === "earnings" ? moneyMeta.earningsScoredOnLabel : moneyMeta.scoredOnLabel) || "Not set"}</span>
                    <button type="button" className="gmbw-rule-change" onClick={() => setTab("setup")}>Setup</button>
                  </div>
                  <p className="gmbw-hint">Change this in Setup, with the pay rules.</p>
                </div>
                {/* Same scope panel as Home — multi-workspace + average/total + date range. */}
                <ScopeWindow draft={opts} onChange={setOpts} fields={["workspaces", "show", "range"]} />
              </>
            )}

            {isLabor && (
              <>
                <div className="gmbw-fld">
                  <label>Departments</label>
                  <div className="gmbw-lockedfield">
                    <span>
                      {laborConfigured(opts)
                        ? `${rowsOf(opts).length} department${rowsOf(opts).length === 1 ? "" : "s"} against ${
                          revenueKpiOf(opts)
                            ? "your Total Revenue metric"
                            : `$${Math.round(revenueOf(opts)).toLocaleString()} revenue`
                        }`
                        : "Not set"}
                    </span>
                    <button type="button" className="gmbw-rule-change" onClick={() => setTab("setup")}>Setup</button>
                  </div>
                  <p className="gmbw-hint">
                    Revenue, and each department&apos;s hours, cost and goal — all in Setup.
                  </p>
                </div>
                {/* Same "Match the dashboard / pin a range" field every other
                    cross-workspace card gets (see the isMoney block above) — hours
                    are read for whichever window this resolves to. */}
                <ScopeWindow draft={opts} onChange={setOpts} fields={["range"]} />
                <LaborFormulaCard />
              </>
            )}

            {isProfit && (
              <>
                <div className="gmbw-fld">
                  <label>Where the numbers come from</label>
                  <div className="gmbw-lockedfield">
                    <span>{profitSummary}</span>
                    <button type="button" className="gmbw-rule-change" onClick={() => setTab("setup")}>Setup</button>
                  </div>
                  <p className="gmbw-hint">
                    Revenue, labor, expenses and every goal — all in Setup.
                  </p>
                </div>
                {/* The same "Match the dashboard / pin a range" field every other
                    cross-workspace card gets — revenue and hours are both read
                    for whichever window this resolves to. */}
                <ScopeWindow draft={opts} onChange={setOpts} fields={["range"]} />
                <ProfitFormulaCard draft={opts} />
              </>
            )}

            {isNetBonus && (
              <>
                <div className="gmbw-fld">
                  <label>What it pays on</label>
                  <div className="gmbw-lockedfield">
                    <span>{netBonusLine}</span>
                    <button type="button" className="gmbw-rule-change" onClick={() => setTab("setup")}>Setup</button>
                  </div>
                  <p className="gmbw-hint">
                    The card the Net profit is read from, and the steps it pays at — all in Setup.
                  </p>
                </div>
                {/* Same "Match the dashboard / pin a range" field the profit card
                    has — the ladder is judged over whichever window this resolves to. */}
                <ScopeWindow draft={opts} onChange={setOpts} fields={["range"]} />
              </>
            )}

            {isDept && (
              <>
                <div className="gmbw-fld">
                  <label>Departments</label>
                  <div className="gmbw-lockedfield">
                    <span>{deptSummary}</span>
                    <button type="button" className="gmbw-rule-change" onClick={() => setTab("setup")}>Setup</button>
                  </div>
                  <p className="gmbw-hint">
                    Which workspaces get a row, and which of each one&apos;s metrics fill its cells — all in Setup.
                  </p>
                </div>
                {/* The same "Match the dashboard / pin a range" field every other
                    cross-workspace card gets — both the amounts and the Yeti's
                    pace are read for whichever window this resolves to. */}
                <ScopeWindow draft={opts} onChange={setOpts} fields={["range"]} />
                <DeptMetricsFormulaCard />
              </>
            )}

            {isAnnual && (
              <>
                <p className="gmbw-hint" style={{ marginTop: 0 }}>
                  Year-1 rollup. Pick <b>at least two workspaces</b> (e.g. Sales + In-House), then map which
                  Scorekeeper metrics feed live quarter bonuses — same Metrics builder as Team Performance vs. The Yeti.
                </p>
                <ScopeWindow draft={opts} onChange={setOpts} fields={["workspaces", "show", "range"]} />
                {multiWs && (
                  <MetricPlanEditor draft={opts} onChange={setOpts} minSeedCols={2} metricCatalog={metricCatalog} />
                )}
              </>
            )}

            {isLeaderboard && (
              <div className="gmbw-fld">
                <label>Who it ranks</label>
                <div className="gmbw-showlist">
                  <button
                    type="button"
                    className={`gmbw-show${!across ? " on" : ""}`}
                    aria-pressed={!across}
                    onClick={() => setOpt({ across: false })}
                  >
                    <span className="gmbw-show-rad" />
                    <span className="gmbw-show-t">
                      <b>One workspace</b>
                      <span>Everyone in a single workspace, ranked by points.</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`gmbw-show${across ? " on" : ""}`}
                    aria-pressed={across}
                    onClick={() => setOpt({ across: true })}
                  >
                    <span className="gmbw-show-rad" />
                    <span className="gmbw-show-t">
                      <b>Compare across workspaces</b>
                      <span>Pool several teams into one ranking — each row tagged with their workspace. Setup: Yeti qualify gate + Closed Rate tie-break.</span>
                    </span>
                  </button>
                </div>
                {!across && (
                  <p className="gmbw-hint">
                    A one-workspace board has nothing to set up — it is the board you already know.
                  </p>
                )}
              </div>
            )}

            {isBoard && (
              <div className="gmbw-fld">
                <label>Workspaces</label>
                <div className="gmbw-showlist">
                  {tops.map((w) => {
                    const picked = (opts.workspaceIds || []).map(String);
                    const on = picked.includes(String(w._id));
                    return (
                      <button
                        key={w._id}
                        type="button"
                        className={`gmbw-show${on ? " on" : ""}`}
                        aria-pressed={on}
                        onClick={() => setOpt({
                          workspaceIds: on
                            ? picked.filter((id) => id !== String(w._id))
                            : [...picked, String(w._id)],
                        })}
                      >
                        <span className="gmbw-show-rad" />
                        <span className="gmbw-show-t">
                          <b style={{ textTransform: "capitalize" }}>{w.name}</b>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="gmbw-hint">
                  {(opts.workspaceIds || []).length > 1
                    ? "Everyone from these workspaces in one ranking, each row tagged with their team. People are still ranked individually — a workspace is never a row."
                    : "Pick more than one to pool them into a single cross-team ranking."}
                </p>
              </div>
            )}

            {/* Cross-workspace leaderboard columns: auto-match by name, or pair
                differently named metrics per workspace into one column — same
                control as Team Performance vs. the Yeti. */}
            {isBoard && multiWs && (
              <MetricPlanEditor draft={opts} onChange={setOpts} metricCatalog={metricCatalog} />
            )}

            {followsSelected && (
              <div className="gmbw-fld">
                <label>Workspace</label>
                <div className="gmbw-lockedfield">
                  <span>Follows the workspace you&apos;re viewing</span>
                </div>
                <p className="gmbw-hint">This component reads the workspace selected in the app.</p>
              </div>
            )}

            {/* Single workspace only for non-scoped, non-custom cards (e.g. plain
                Beat the Yeti on one team). Custom + money + scope widgets already
                pick teams above — a second "Workspace" dropdown was leftover UX
                that forced one pick after you'd already chosen two. */}
            {!followsSelected && !across && !def?.scope && !isMoney && !isAnnual && !isLabor && !isProfit && !isNetBonus && !isDept
              && !(isCustomId(widget.comp) || def?.custom) && (
              <div className="gmbw-fld">
                <label htmlFor="dbce-ws">Workspace</label>
                <select
                  id="dbce-ws"
                  value={String(opts.workspaceId || "")}
                  onChange={(e) => setOpt({ workspaceId: e.target.value })}
                >
                  {workspaces.map((w) => (
                    <option key={w._id} value={String(w._id)}>
                      {w.parentId ? `— ${w.name}` : w.name}
                    </option>
                  ))}
                </select>
                <p className="gmbw-hint">Whose numbers this card shows.</p>
              </div>
            )}

          </>
        )}

        {tab === "setup" && isBoard && (
          <LeaderboardSetup draft={opts} onChange={(next) => setOpt(next)} />
        )}

        {tab === "setup" && isLabor && (
          <LaborSetup draft={opts} onChange={setOpts} />
        )}

        {tab === "setup" && isProfit && (
          /* Full replace, not a merge — same reason AnnualCompSetup takes
             setOpts: a stale { ...draft } from a merge can overwrite the goals
             or expense list this panel just rewrote. */
          <ProfitTrendSetup draft={opts} onChange={setOpts} boardWidgets={boardWidgets} />
        )}

        {tab === "setup" && isNetBonus && (
          /* Full replace, not a merge — same reason ProfitTrendSetup takes
             setOpts: a stale { ...draft } from a merge can overwrite the ladder
             this panel just rewrote. */
          <NetProfitBonusSetup draft={opts} onChange={setOpts} boardWidgets={boardWidgets} />
        )}

        {tab === "setup" && isDept && (
          /* Full replace, not a merge — same reason AnnualCompSetup takes
             setOpts: a stale { ...draft } from a merge can overwrite the
             per-row metric map this panel just rewrote. */
          <DeptMetricsSetup draft={opts} onChange={setOpts} />
        )}

        {tab === "setup" && isAnnual && (
          <AnnualCompSetup
            draft={opts}
            /* Full replace — same as ScopeWindow / MetricPlanEditor. setOpt
               (merge) let a stale { ...draft } overwrite workspaceIds + metricPlan
               from Basics and made Save look broken. */
            onChange={setOpts}
            metricCatalog={metricCatalog || []}
          />
        )}

        {tab === "setup" && isMoney && !moneyWsIds.length && (
          <p className="gmbw-hint" style={{ margin: 0 }}>Pick at least one workspace under Basics, then set pay rules for each team here.</p>
        )}
        {tab === "setup" && isMoney && moneyLoading && (
          <p className="gmbw-hint" style={{ margin: 0 }}>Loading pay rules…</p>
        )}
        {/* Multi-ws Anticipated earnings: ONE combined Setup (metric per workspace),
            not a copy of Commission/Bonuses under every workspace header. */}
        {tab === "setup" && isMoney && !moneyLoading && def?.money === "earnings" && moneyWsIds.length >= 2 && (
          <CombinedEarningsSetup
            combinedPay={combinedPayLive}
            workspaceIds={moneyWsIds}
            workspaceNames={combinedWsNames}
            metricsByWs={combinedMetricsByWs}
            onChange={onCombinedPayChange}
          />
        )}
        {/* Single-ws earnings, or any ladder: still per-workspace Setup. */}
        {tab === "setup" && isMoney && !moneyLoading && !(def?.money === "earnings" && moneyWsIds.length >= 2) && moneyWsIds.map((wsId) => {
          const pack = moneyPacks[wsId];
          const ws = workspaces.find((w) => String(w._id) === wsId);
          if (!pack) return null;
          return (
            <div key={wsId} className="gmbw-money-ws">
              {moneyWsIds.length > 1 && (
                <div className="gmbw-money-ws-h">{ws?.name || "Workspace"}</div>
              )}
              {def.money === "earnings" ? (
                <EarningsSetup
                  program={pack.program}
                  metrics={pack.metrics}
                  onChange={(next) => onMoneyChange(wsId, next)}
                />
              ) : (
                <LadderSetup
                  program={pack.program}
                  metrics={pack.metrics}
                  ytd={pack.ytd}
                  onChange={(next) => onMoneyChange(wsId, next)}
                />
              )}
              <p className="gmbw-hint" style={{ marginTop: 8 }}>
                These rules apply only to <b>this card on this dashboard</b>
                {ws?.name ? <> (using {ws.name} numbers)</> : null}
                — not the workspace homepage or other Analytics boards.
                {moneyWsIds.length > 1 && (
                  <> &quot;Reached&quot; below uses the <b>team average YTD</b> on this workspace&apos;s scored-on metric — same roll-up the card shows, not your personal admin YTD.</>
                )}
              </p>
            </div>
          );
        })}

        {tab === "appearance" && (
          <>
            {(isCustomId(widget.comp) || def?.custom) && (
              <CustomLayout
                draft={opts}
                metricCatalog={metricCatalog || []}
                onChange={(next) => setOpt(next)}
              />
            )}

            {isGoalCard && (
              <>
                <div className="gmbw-fld">
                  <label>Chart style</label>
                  {/* The SAME picker Build Your Own's own Visualisation tab
                      uses (GraphPicker, CustomizeDrawer.jsx) — Kelvin's ask
                      was literally to adapt that picker here, not redraw a
                      lookalike. Number/Target bar/Ring/Donut/Gauge render via
                      GoalShape (the exact shape components those cards use,
                      exported from CustomWidget); Area/Line/Bar/Sparkline
                      still plot the running total in GoalTrendChart. */}
                  <GraphPicker value={opts.chartStyle || "area"} onChange={(g) => setOpt({ chartStyle: g })} />
                </div>
                {/* Both choices below only change how the TREND is drawn, so
                    they're hidden for the single-value shapes (Number / Target
                    bar / Ring / Donut / Gauge — GoalShape.jsx), which have no
                    days and no line to pace. */}
                {GOAL_SERIES_STYLES.includes(opts.chartStyle || "area") && (
                  <>
                    <div className="gmbw-fld">
                      <label>What the chart plots</label>
                      <div className="gmbw-showlist">
                        <button
                          type="button"
                          className={`gmbw-show${(opts.chartView || "cumulative") !== "daily" ? " on" : ""}`}
                          aria-pressed={(opts.chartView || "cumulative") !== "daily"}
                          onClick={() => setOpt({ chartView: "cumulative" })}
                        >
                          <span className="gmbw-show-rad" />
                          <span className="gmbw-show-t">
                            <b>Running total</b>
                            <span>Every day added to the last, climbing toward the goal over the period.</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`gmbw-show${opts.chartView === "daily" ? " on" : ""}`}
                          aria-pressed={opts.chartView === "daily"}
                          onClick={() => setOpt({ chartView: "daily" })}
                        >
                          <span className="gmbw-show-rad" />
                          <span className="gmbw-show-t">
                            <b>That day only</b>
                            <span>Each day on its own, against the goal&apos;s per-working-day target.</span>
                          </span>
                        </button>
                      </div>
                      <p className="gmbw-hint">
                        The number above the chart is the period total either way — this is only what the line or bars are drawn from.
                      </p>
                    </div>
                    <div className="gmbw-fld">
                      <label>Days on the chart</label>
                      <div className="gmbw-showlist">
                        <button
                          type="button"
                          className={`gmbw-show${(opts.chartDays || "all") !== "working" ? " on" : ""}`}
                          aria-pressed={(opts.chartDays || "all") !== "working"}
                          onClick={() => setOpt({ chartDays: "all" })}
                        >
                          <span className="gmbw-show-rad" />
                          <span className="gmbw-show-t">
                            <b>Include weekends &amp; holidays</b>
                            <span>Every day of the period gets its own point on the chart.</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`gmbw-show${opts.chartDays === "working" ? " on" : ""}`}
                          aria-pressed={opts.chartDays === "working"}
                          onClick={() => setOpt({ chartDays: "working" })}
                        >
                          <span className="gmbw-show-rad" />
                          <span className="gmbw-show-t">
                            <b>Working days only</b>
                            <span>
                              Mon–Fri minus company holidays — the same days the pace is divided by.
                              No run of empty bars between every week.
                            </span>
                          </span>
                        </button>
                      </div>
                      <p className="gmbw-hint">
                        Takings on a weekend or a holiday always count in the number above the chart.
                        On a running total they are in the line too — the next working day&apos;s step
                        carries them. Day by day they have no bar of their own, so the caption under the
                        chart adds them up (&quot;+ $1,240 off-days&quot;) rather than pinning them on a
                        day that didn&apos;t earn them.
                      </p>
                    </div>
                    {isScheduledMetric && LOOK_AHEAD_STYLES.includes(opts.chartStyle || "area") && (
                      <div className="gmbw-fld">
                        <label>Look ahead</label>
                        <div className="gmbw-showlist">
                          {[0, ...LOOK_AHEAD_OPTIONS].map((n) => {
                            const on = Number(opts.lookAhead || 0) === n;
                            return (
                              <button
                                key={n}
                                type="button"
                                className={`gmbw-show${on ? " on" : ""}`}
                                aria-pressed={on}
                                onClick={() => setOpt({ lookAhead: n })}
                              >
                                <span className="gmbw-show-rad" />
                                <span className="gmbw-show-t">
                                  <b>{n === 0 ? "Off" : `Next ${n} days`}</b>
                                  <span>
                                    {n === 0
                                      ? "Only the days of the period."
                                      : `Also show the ${n} days after the period ends, faded, so the first bookings of next month are visible.`}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        <p className="gmbw-hint">
                          The extra days are not part of the total or the goal above the chart — they are
                          listed on their own under it.
                          {opts.chartView !== "daily" && Number(opts.lookAhead || 0) > 0
                            ? " They only show in the “That day only” view."
                            : ""}
                        </p>
                      </div>
                    )}
                    <div className="gmbw-fld">
                      <label>Goal line</label>
                      <div className="gmbw-showlist">
                        <button
                          type="button"
                          className={`gmbw-show${(opts.goalLineMode || "pace") !== "flat" ? " on" : ""}`}
                          aria-pressed={(opts.goalLineMode || "pace") !== "flat"}
                          onClick={() => setOpt({ goalLineMode: "pace" })}
                        >
                          <span className="gmbw-show-rad" />
                          <span className="gmbw-show-t">
                            <b>Pace</b>
                            <span>
                              The goal split over the period&apos;s working days (Mon–Fri, minus company holidays),
                              so the line climbs across the month and lands on the goal on the last working day.
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`gmbw-show${opts.goalLineMode === "flat" ? " on" : ""}`}
                          aria-pressed={opts.goalLineMode === "flat"}
                          onClick={() => setOpt({ goalLineMode: "flat" })}
                        >
                          <span className="gmbw-show-rad" />
                          <span className="gmbw-show-t">
                            <b>Flat target</b>
                            <span>One dashed rule at the goal itself — the full period&apos;s number, never divided into a per-day figure, in both the running-total and day-by-day views.</span>
                          </span>
                        </button>
                      </div>
                      <p className="gmbw-hint">
                        Pace draws out to the end of the period, so a gap with days still to go is visible.
                        A percentage metric always uses the flat rule — a rate isn&apos;t earned a day at a time.
                      </p>
                    </div>
                    {/* Sparkline draws no axis, so there is nothing to space. */}
                    {(opts.chartStyle || "area") !== "spark" && (
                      <YStepField
                        value={opts.yStep}
                        kind={metricCatalog.find((m) => m.id === (opts.metrics && opts.metrics[0] && opts.metrics[0].metricId))?.kind}
                        goal={opts.goalTarget}
                        view={opts.chartView}
                        onChange={(v) => setOpt({ yStep: v })}
                      />
                    )}
                  </>
                )}
                <LineColorSwatches
                  label="Progress line"
                  value={opts.lineColor}
                  defaultColor="#1B598E"
                  onChange={(c) => setOpt({ lineColor: c })}
                />
                <LineColorSwatches
                  label="Goal line"
                  value={opts.goalColor}
                  defaultColor="#D97706"
                  onChange={(c) => setOpt({ goalColor: c })}
                />
              </>
            )}

            <div className="gmbw-fld">
              <label>Scene</label>
              {scenes.length > 1 ? (
                <>
                  <div className="gmbw-scenes">
                    {scenes.map((sc) => {
                      const Th = sc.thumb;
                      const on = (opts.scene || def.defaultScene) === sc.key;
                      return (
                        <button
                          key={sc.key}
                          type="button"
                          className={`gmbw-scene${on ? " on" : ""}`}
                          aria-pressed={on}
                          onClick={() => setOpt({ scene: sc.key })}
                        >
                          <span className="gmbw-scene-th">{Th ? <Th tiny /> : null}</span>
                          <span className="gmbw-scene-lb">{sc.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="gmbw-hint">The same numbers, drawn a different way.</p>
                </>
              ) : (
                <p className="gmbw-hint">This component has one look.</p>
              )}
            </div>

            {isBoard && Array.isArray(board?.parts) && board.parts.length > 0 && (
              <div className="gmbw-fld">
                <label>What to show</label>
                <div className="gmbw-toggles">
                  {board.parts.map((part, i) => {
                    const shown = !(opts.hide && opts.hide[part.key]);
                    /* Team and the filter mean nothing on a single workspace. */
                    const off = part.needs === "multi" && live.count < 2;
                    /* "metrics" is the one line written at render time — the
                       workspaces own the metric list, so it is read, never
                       spelled out in the editor. */
                    let { note } = part;
                    if (part.note === "metrics") {
                      note = live.columns.length
                        ? `A column for each metric these workspaces track: ${live.columns.map((c) => c.label).join(", ")}.`
                        : "These workspaces track no metric in common — nothing to put in columns.";
                    }
                    const newGroup = part.group && (i === 0 || board.parts[i - 1].group !== part.group);
                    return (
                      <div key={part.key} className="gmbw-toggle-block">
                        {newGroup && <div className="gmbw-toggle-grp">{part.group}</div>}
                        <div className={`gmbw-toggle${note ? " noted" : ""}`}>
                          <span>
                            {part.label}
                            {note && <i className="gmbw-toggle-note">{note}</i>}
                          </span>
                          <div className="gmbw-toggle-ctl">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={shown && !off}
                              aria-label={part.label}
                              disabled={off}
                              className={`gmbw-switch${shown && !off ? " on" : ""}${off ? " off" : ""}`}
                              onClick={() => setOpt({ hide: { ...(opts.hide || {}), [part.key]: shown } })}
                            >
                              <span className="gmbw-switch-dot" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="gmbw-hint">Hiding something changes the card, never the numbers.</p>
              </div>
            )}

            {isProfit && ["revenue", "expenses", "labor", "net", "pace", "loss"].map((key) => (
              <LineColorSwatches
                key={key}
                label={`${(opts.labels && opts.labels[key]) || PROFIT_SERIES[key].label} line`}
                value={(opts.seriesColors && opts.seriesColors[key]) || null}
                defaultColor={PROFIT_SERIES[key].color}
                onChange={(c) => setOpt({ seriesColors: { ...(opts.seriesColors || {}), [key]: c } })}
              />
            ))}

            {isDept && <DeptTileStyle draft={opts} onChange={setOpts} />}

            {/* Victor: Anticipated earnings (and any card with def.parts) — chip /
                row toggles + optional rename. Already honored by the card via
                hide/labels; the Analytics editor just never offered them. */}
            {Array.isArray(def?.parts) && def.parts.length > 0 && (
              <div className="gmbw-fld">
                <label>What to show</label>
                <div className="gmbw-toggles">
                  {def.parts.map((part) => {
                    const shown = !(opts.hide && opts.hide[part.key]);
                    const labelVal = (opts.labels && opts.labels[part.key]) || "";
                    const canEdit = !!part.editable && shown;
                    const editing = canEdit && (openLabels[part.key] || !!labelVal);
                    return (
                      <div key={part.key} className="gmbw-toggle-block">
                        <div className="gmbw-toggle">
                          <span>{part.label}</span>
                          <div className="gmbw-toggle-ctl">
                            {canEdit && (
                              <button
                                type="button"
                                className={`gmbw-editbtn${editing ? " on" : ""}`}
                                aria-label={`Edit title for ${part.label}`}
                                aria-pressed={editing}
                                title="Edit title"
                                onClick={() => toggleLabel(part.key)}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              role="switch"
                              aria-checked={shown}
                              aria-label={part.label}
                              className={`gmbw-switch${shown ? " on" : ""}`}
                              onClick={() => setOpt({ hide: { ...(opts.hide || {}), [part.key]: shown } })}
                            >
                              <span className="gmbw-switch-dot" />
                            </button>
                          </div>
                        </div>
                        {editing && (
                          <input
                            type="text"
                            className="gmbw-title-input"
                            value={labelVal}
                            placeholder={part.editable}
                            maxLength={40}
                            /* eslint-disable-next-line jsx-a11y/no-autofocus */
                            autoFocus={!!openLabels[part.key]}
                            aria-label={`Title for ${part.label}`}
                            onChange={(e) => setOpt({
                              labels: { ...(opts.labels || {}), [part.key]: e.target.value },
                            })}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="gmbw-hint">Hiding something changes the card, never the numbers. Pencil renames a chip.</p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="gmbw-edit-ft">
        <button type="button" className="gmbw-btn" onClick={cancel}>Cancel</button>
        <button
          type="button"
          className={`gmbw-btn primary${flash ? " saved" : ""}`}
          onClick={save}
          aria-live="polite"
        >
          {flash ? "Saved ✓" : "Save changes"}
        </button>
      </div>
    </>
  );
}

ComponentEditor.propTypes = {
  widget: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  metricCatalog: PropTypes.array,
  /* The board's other components — Revenue, Expenses & Labor's Setup lists the
     Payroll vs Revenue cards among them so Labor can be pointed at one. */
  boardWidgets: PropTypes.array,
};
